import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";

export default function ShopPage() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  // Remember where the customer is browsing so "Continue shopping" brings them back here
  useEffect(() => {
    sessionStorage.setItem("btm_last_shop", window.location.pathname + window.location.search);
  }, [category, q]);
  const { categories, catName } = useCategories();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [specialsOnly, setSpecialsOnly] = useState(searchParams.get("special") === "1");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(600);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { sort, limit: 600 };
    if (category) params.category = category;
    if (q) params.q = q;
    if (specialsOnly) params.special = true;
    if (inStockOnly) params.in_stock = true;
    if (maxPrice < 600) params.max_price = maxPrice;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data.products))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q, sort, specialsOnly, inStockOnly, maxPrice]);

  const title = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (specialsOnly) return "Specials";
    return category ? catName(category) : "All products";
  }, [q, category, specialsOnly, catName]);

  const filterControls = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Categories</p>
        <div className="space-y-1">
          <Link
            to="/shop"
            className={`block text-sm px-3 py-2 rounded-lg transition-colors ${!category ? "bg-navy text-white font-semibold" : "hover:bg-slate-100"}`}
            data-testid="filter-cat-all"
          >
            All products
          </Link>
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              onClick={() => setFiltersOpen(false)}
              className={`block text-sm px-3 py-2 rounded-lg transition-colors ${category === c.slug ? "bg-navy text-white font-semibold" : "hover:bg-slate-100"}`}
              data-testid={`filter-cat-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Max price · {maxPrice >= 600 ? "Any" : `R${maxPrice}`}</p>
        <input
          type="range"
          min="20"
          max="600"
          step="10"
          value={maxPrice}
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          className="w-full accent-[#E8722A]"
          data-testid="filter-price-range"
        />
      </div>
      <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="filter-specials-only">
        <input
          type="checkbox"
          checked={specialsOnly}
          onChange={(e) => setSpecialsOnly(e.target.checked)}
          className="w-4 h-4 accent-[#E8722A]"
        />
        Specials only
      </label>
      <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="filter-in-stock">
        <input
          type="checkbox"
          checked={inStockOnly}
          onChange={(e) => setInStockOnly(e.target.checked)}
          className="w-4 h-4 accent-[#E8722A]"
        />
        In stock only
      </label>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="shop-page">
      <Seo title={title} description={`Shop ${title} online at BTMEDU — search, filter and order school supplies with delivery or collection.`} />
      <Reveal>
        {(category || q) && (
          <div className="mb-4">
            <BackButton label="Back" testId="shop-back-button" />
          </div>
        )}
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand mb-2">Catalogue</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">{title}</h1>
      </Reveal>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setFiltersOpen(true)}
          className="lg:hidden flex items-center gap-2 text-sm font-semibold border border-slate-200 rounded-full px-4 py-2 bg-white"
          data-testid="open-filters-button"
        >
          <SlidersHorizontal size={15} /> Filters
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-sm border border-slate-200 rounded-full px-4 py-2 bg-white font-medium outline-none"
          data-testid="sort-select"
        >
          <option value="featured">Featured first</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
          <option value="name">Name A–Z</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto" data-testid="results-count">
          {products.length} product{products.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="mt-8 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block bg-white rounded-2xl border border-slate-200/80 p-5 h-max sticky top-36" data-testid="filters-sidebar">
          {filterControls}
        </aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 aspect-[3/4] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-16 text-center" data-testid="no-results">
              <p className="font-display font-bold text-navy text-lg">Nothing matches those filters</p>
              <p className="text-sm text-slate-500 mt-2">Try widening the price range or clearing filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5" data-testid="product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-navy-dark/60" onClick={() => setFiltersOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white p-6 overflow-y-auto" data-testid="mobile-filters">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-navy">Filters</h3>
              <button onClick={() => setFiltersOpen(false)} data-testid="close-filters-button" aria-label="Close filters">
                <X size={18} />
              </button>
            </div>
            {filterControls}
          </div>
        </div>
      )}
    </div>
  );
}
